import React, { useMemo } from 'react';
import { useEditor, useNode } from '@craftjs/core';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { useColor } from '@/hooks/useColor';
import { CONTENT_BLOCK_NAME } from '@builder4/shared/utils/constants';
import { SliderField } from '../SliderField/SliderField';
import { ColorField } from '../ColorField/ColorField';
import { SegmentedControl } from '../SegmentedControl';
import {
  SHAPE_DIVIDER_DEFS,
  SHAPE_DIVIDER_DEFAULTS,
  SHAPE_DIVIDER_TYPES,
  type ShapeDividerType,
} from '@builder4/shared/components/Container/shapes';
import { resolveAutoShapeDividerColor } from '@builder4/shared/components/Container/ShapeDivider';
import type { Color } from '@builder4/shared/types/layout';
import { isColorReference } from '@builder4/shared/utils/styleHelpers';
import { useAppSelector } from '@/store/hooks';
import { selectActiveGlobalStyle } from '@/store/globalStyles/selectors';
import { createPaletteFromGlobalColors } from '@/utils/colorPaletteHelpers';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';

type ShapeDividerNodeProps = {
  shapeDividerType?: 'none' | ShapeDividerType;
  shapeDividerPosition?: 'top' | 'bottom';
  shapeDividerHeight?: number;
  shapeDividerColor?: Color | string;
  shapeDividerFrequency?: number;
  shapeDividerFlip?: boolean;
  shapeDividerCurveCenter?: number;
};

const SHAPE_DIVIDER_LABELS: Record<ShapeDividerType, string> = {
  wave: 'Wave',
  zigzag: 'Zigzag',
  curve: 'Curve',
  bowl: 'Bowl',
  tilt: 'Tilt',
};

export const NodeShapeDividerSettings: React.FC = () => {
  const activeStyles = useAppSelector(selectActiveGlobalStyle);
  const { query } = useEditor();

  const {
    id,
    nodeProps,
    actions: { setProp },
  } = useNode((node) => ({
    nodeProps: node.data.props as Partial<ShapeDividerNodeProps>,
  }));

  const { getBPProp, setBPProp } = useBreakpointAwareProp(CONTENT_BLOCK_NAME);

  const shapeDividerType = getBPProp(nodeProps, 'shapeDividerType') ?? 'none';
  const shapeDividerPosition = getBPProp(nodeProps, 'shapeDividerPosition') ?? 'bottom';
  const shapeDividerHeight = getBPProp(nodeProps, 'shapeDividerHeight') ?? 80;
  const shapeDividerColor = getBPProp(nodeProps, 'shapeDividerColor');
  const shapeDividerFrequency =
    getBPProp(nodeProps, 'shapeDividerFrequency') ??
    (shapeDividerType !== 'none' ? SHAPE_DIVIDER_DEFAULTS[shapeDividerType] : 1);
  const shapeDividerFlip = getBPProp(nodeProps, 'shapeDividerFlip') ?? false;
  const shapeDividerCurveCenter = getBPProp(nodeProps, 'shapeDividerCurveCenter') ?? 50;

  const isActive = shapeDividerType !== 'none';
  const showFrequency =
    isActive &&
    shapeDividerType !== 'tilt' &&
    shapeDividerType !== 'curve' &&
    shapeDividerType !== 'bowl';
  const showCurveCenter = isActive && (shapeDividerType === 'curve' || shapeDividerType === 'bowl');

  const autoColor = useMemo(
    () => resolveAutoShapeDividerColor(id, shapeDividerPosition, query, activeStyles.colors),
    [id, shapeDividerPosition, query, activeStyles.colors],
  );

  // The effective color: explicit or auto-resolved
  const effectiveColor = shapeDividerColor || autoColor;
  const colorResult = useColor(effectiveColor, activeStyles.colors);

  const handleTypeChange = (type: 'none' | ShapeDividerType) => {
    setProp((props: Partial<ShapeDividerNodeProps>) => {
      setBPProp(props, 'shapeDividerType', type);
    });
  };

  const handleColorChange = (value: Color | undefined) => {
    setProp((props: Partial<ShapeDividerNodeProps>) => {
      setBPProp(props, 'shapeDividerColor', value ? JSON.parse(JSON.stringify(value)) : undefined);
    }, 500);
  };

  const handleColorChangeComplete = (newColor: Color | string, initialColor: Color | string) => {
    // Reset to initial for undo
    setProp((props: Partial<ShapeDividerNodeProps>) => {
      const initialValue = initialColor
        ? isColorReference(initialColor)
          ? initialColor
          : JSON.parse(JSON.stringify(initialColor))
        : undefined;
      setBPProp(props, 'shapeDividerColor', initialValue);
    });
    // Set new value
    setProp((props: Partial<ShapeDividerNodeProps>) => {
      const newValue = newColor
        ? isColorReference(newColor)
          ? newColor
          : JSON.parse(JSON.stringify(newColor))
        : undefined;
      setBPProp(props, 'shapeDividerColor', newValue);
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <SelectField
        text="Shape"
        defaultValue={shapeDividerType}
        onValueChange={(value) => handleTypeChange(value as 'none' | ShapeDividerType)}
        size="full"
      >
        <SelectItem value="none">None</SelectItem>
        {SHAPE_DIVIDER_TYPES.map((type) => {
          const def = SHAPE_DIVIDER_DEFS[type];
          return (
            <SelectItem key={type} value={type}>
              <span className="flex items-center gap-2">
                <svg viewBox={def.viewBox} preserveAspectRatio="none" className="h-4 w-8 shrink-0">
                  <path d={def.path} fill="currentColor" />
                </svg>
                {SHAPE_DIVIDER_LABELS[type]}
              </span>
            </SelectItem>
          );
        })}
      </SelectField>

      {isActive && (
        <div className="flex flex-col gap-3">
          <SegmentedControl<'top' | 'bottom'>
            text="Position"
            value={shapeDividerPosition}
            options={['top', 'bottom']}
            onChange={(value) => {
              setProp((props: Partial<ShapeDividerNodeProps>) => {
                setBPProp(props, 'shapeDividerPosition', value);
              });
            }}
          />

          <SegmentedControl<'left' | 'right'>
            text="Direction"
            value={shapeDividerFlip ? 'right' : 'left'}
            options={['left', 'right']}
            onChange={(value) => {
              setProp((props: Partial<ShapeDividerNodeProps>) => {
                setBPProp(props, 'shapeDividerFlip', value === 'right');
              });
            }}
          />

          <SliderField
            label="Height"
            value={shapeDividerHeight}
            onChange={(value) => {
              setProp((props: Partial<ShapeDividerNodeProps>) => {
                setBPProp(props, 'shapeDividerHeight', value);
              }, 500);
            }}
            min={20}
            max={300}
            step={1}
            suffix="px"
          />

          {showFrequency && (
            <SliderField
              label="Frequency"
              value={shapeDividerFrequency}
              onChange={(value) => {
                setProp((props: Partial<ShapeDividerNodeProps>) => {
                  setBPProp(props, 'shapeDividerFrequency', value);
                }, 500);
              }}
              min={1}
              max={20}
              step={1}
            />
          )}

          {showCurveCenter && (
            <SliderField
              label="Center"
              value={shapeDividerCurveCenter}
              onChange={(value) => {
                setProp((props: Partial<ShapeDividerNodeProps>) => {
                  setBPProp(props, 'shapeDividerCurveCenter', value);
                }, 500);
              }}
              min={0}
              max={100}
              step={1}
              suffix="%"
            />
          )}

          <ColorField
            palettes={[createPaletteFromGlobalColors(activeStyles.colors)]}
            className="max-w-[216px]"
            text="Color"
            mode={colorResult.mode}
            gradient={colorResult.gradient}
            inputValue={colorResult.hexForInput}
            placeholderText="Auto"
            color={colorResult.resolvedColor ?? undefined}
            onChange={handleColorChange}
            onChangeComplete={handleColorChangeComplete}
            hasGradient
            canClear
          />
        </div>
      )}
    </div>
  );
};
